import React from 'react';
import { Link } from 'react-router-dom';
import { Bike, ArrowRight } from 'lucide-react';
import { useSection } from '../../hooks/useSection';

const STATS = [
  { label: 'Revenus jour', val: '87 500', unit: 'F' },
  { label: 'Courses', val: '142', unit: '' },
  { label: 'Actives', val: '5', unit: '/5' },
];

const FLEET = [
  { id: 'SUNU-001', driver: 'Cheikh M.', rev: '21 300', status: 'En course', color: '#D4A574' },
  { id: 'SUNU-002', driver: 'Aminata D.', rev: '18 750', status: 'En course', color: '#D4A574' },
  { id: 'OMEGA-001', driver: 'Modou S.', rev: '24 100', status: 'Recharge', color: '#f59e0b' },
];

const DEFAULT_BULLETS = ['Vous restez propriétaire de vos motos', 'Proxy gère tout l’opérationnel', 'Suivi temps réel via FLIT'];

const ProxyFleetPromo: React.FC = () => {
  const { section } = useSection('home_fleet_promo');
  if (section && section.is_visible === false) return null;
  const ed = section?.extra_data || {};
  const badge = section?.subtitle || 'Nouveau — Proxy Fleet';
  const paragraph = section?.content || 'Acquérez vos motos Proxy. Restez propriétaire. Proxy Fleet recrute les conducteurs, met les motos sur Yango, et vous donne un suivi temps réel via FLIT.';
  const bullets: string[] = ed.bullets?.length ? ed.bullets : DEFAULT_BULLETS;
  const btnLabel = section?.button_label || 'Découvrir Proxy Fleet';
  const btnUrl = section?.button_url || '/proxy-fleet';
  const cta2 = ed.cta_secondary || { label: 'Simuler', link: '/proxy-fleet#simulateur' };
  return (
  <section className="relative overflow-hidden bg-white py-14 sm:py-20 lg:py-24">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-[2rem] bg-[#0A1628] p-6 sm:p-10 lg:p-14">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 left-[8%] h-80 w-80 rounded-full bg-[#D4A574]/[0.08] blur-3xl" />
          <div className="absolute bottom-0 right-[6%] h-96 w-96 rounded-full bg-[#1E3A5F]/40 blur-3xl" />
        </div>
        <div className="relative grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="flex flex-col justify-center lg:col-span-5">
            <div className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-[#D4A574]/35 bg-[#D4A574]/15 px-3 py-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4A574]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D4A574]">{badge}</span>
            </div>
            <h2 className="mb-4 text-3xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-4xl lg:text-5xl">
              Votre flotte de motos électriques,
              <br />
              <span className="text-[#D4A574]">gérée pour vous</span>
            </h2>
            <p className="mb-7 max-w-md text-sm leading-relaxed text-white/65 sm:text-base">
              {paragraph}
            </p>
            <div className="mb-7 space-y-2.5">
              {bullets.map((line, e) => (
                <div key={e} className="flex items-center gap-2.5 text-sm text-white/80">
                  <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#D4A574]/15">
                    <span className="h-1 w-1 rounded-full bg-[#D4A574]" />
                  </span>
                  {line}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to={btnUrl}
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#D4A574] px-6 py-3 text-sm font-bold text-[#0A1628] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#E5BD8B]"
              >
                {btnLabel}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to={cta2.link || '/proxy-fleet#simulateur'}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:bg-white/10"
              >
                {cta2.label || 'Simuler'}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white/[0.04] p-4 ring-1 ring-white/10 backdrop-blur-sm sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/50">Ma flotte</div>
                  <div className="text-sm font-black text-white">5 motos · Dakar</div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#D4A574]/15 px-2.5 py-1 ring-1 ring-[#D4A574]/30">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4A574]" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#D4A574]">En ligne</span>
                </div>
              </div>
              <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
                {STATS.map((stat, e) => (
                  <div key={e} className="rounded-xl bg-white/[0.04] p-2.5 ring-1 ring-white/[0.06] sm:p-3">
                    <div className="text-[8px] font-bold uppercase tracking-wider text-white/50 sm:text-[9px]">{stat.label}</div>
                    <div className="mt-0.5 text-base font-black tracking-tight text-white tabular-nums sm:text-lg">
                      {stat.val}
                      <span className="text-[10px] font-bold text-white/40">{stat.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-white/50">Détail par moto</div>
              <div className="space-y-1.5">
                {FLEET.map((moto, e) => (
                  <div key={e} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      <Bike size={14} className="shrink-0" style={{ color: moto.color }} strokeWidth={2.2} />
                      <div>
                        <div className="text-[11px] font-black text-white sm:text-xs">{moto.id}</div>
                        <div className="text-[9px] text-white/50 sm:text-[10px]">Conducteur · {moto.driver}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-black text-white tabular-nums sm:text-xs">{moto.rev} F</div>
                      <div className="text-[9px] font-semibold sm:text-[10px]" style={{ color: moto.color }}>
                        {moto.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-center text-[9px] text-white/35">Aperçu illustratif — chiffres simulés</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

export default ProxyFleetPromo;
